import { useRef } from 'react';
import { Controller, useWatch, type Control, type UseFormSetValue } from 'react-hook-form';
import { Input, Switch } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import type { EditAgentFormValues } from './voiceAgentEditFormTypes';
import { AdvanceSlider } from './VoiceAgentAdvanceSlider';

const MAX_PROMPT_TEMPLATES = 3;

interface VoiceAgentIdleTimeoutSectionProps {
  control: Control<EditAgentFormValues>;
  setValue: UseFormSetValue<EditAgentFormValues>;
}

export function VoiceAgentIdleTimeoutSection({
  control,
  setValue,
}: Readonly<VoiceAgentIdleTimeoutSectionProps>) {
  const enabled = useWatch({ control, name: 'idle_timeout.enabled', defaultValue: false });
  const prompts =
    useWatch({ control, name: 'idle_timeout.prompt_templates', defaultValue: [] }) ?? [];
  const nextPromptKey = useRef(0);
  const promptKeys = useRef<string[]>([]);
  while (promptKeys.current.length < prompts.length) {
    promptKeys.current.push(`idle-timeout-prompt-${nextPromptKey.current++}`);
  }
  promptKeys.current.length = prompts.length;

  const updatePrompts = (nextPrompts: string[]) => {
    setValue('idle_timeout.prompt_templates', nextPrompts.slice(0, MAX_PROMPT_TEMPLATES), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  return (
    <div className="rounded-xl border border-divider bg-content1">
      <div className="flex items-start justify-between gap-4 p-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 rounded-lg bg-default-100 p-2 text-primary">
            <Icon icon="solar:clock-circle-linear" width={20} />
          </div>
          <div>
            <p className="font-medium text-foreground">Idle Timeout</p>
            <p className="mt-0.5 text-sm text-default-500">
              Prompt the caller again when no response is detected.
            </p>
          </div>
        </div>
        <Controller
          name="idle_timeout.enabled"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <Switch
              aria-label="Enable idle timeout"
              isSelected={field.value ?? false}
              onValueChange={field.onChange}
            />
          )}
        />
      </div>

      {enabled && (
        <div className="space-y-5 border-t border-divider p-4">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Controller
              name="idle_timeout.timeout_seconds"
              control={control}
              defaultValue={10}
              render={({ field }) => (
                <AdvanceSlider
                  label="Timeout"
                  ariaLabel="Timeout seconds"
                  minValue={1}
                  maxValue={30}
                  step={1}
                  value={field.value ?? 10}
                  onChange={field.onChange}
                  valueSuffix=" s"
                  showSteps
                />
              )}
            />
            <Controller
              name="idle_timeout.retries"
              control={control}
              defaultValue={1}
              render={({ field }) => (
                <AdvanceSlider
                  label="Retries"
                  ariaLabel="Idle timeout retries"
                  minValue={1}
                  maxValue={3}
                  step={1}
                  value={field.value ?? 1}
                  onChange={field.onChange}
                  showSteps
                />
              )}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-foreground">Prompt templates</p>
                <p className="text-xs text-default-500">
                  Messages used for each retry. Add up to three.
                </p>
              </div>
              <CtyButton
                size="sm"
                variant="flat"
                color="primary"
                startContent={<Icon icon="solar:add-circle-linear" width={18} />}
                onPress={() => updatePrompts([...prompts, ''])}
                isDisabled={prompts.length >= MAX_PROMPT_TEMPLATES}
              >
                Add prompt
              </CtyButton>
            </div>

            {prompts.length === 0 ? (
              <div className="rounded-lg border border-dashed border-divider px-4 py-5 text-center text-sm text-default-500">
                No prompt templates added.
              </div>
            ) : (
              <div className="space-y-2">
                {prompts.map((prompt, index) => (
                  <div key={promptKeys.current[index]} className="flex items-center gap-2">
                    <Input
                      aria-label={`Idle timeout prompt ${index + 1}`}
                      placeholder={`Prompt ${index + 1}`}
                      value={prompt}
                      onValueChange={(value) =>
                        updatePrompts(prompts.map((item, itemIndex) => (itemIndex === index ? value : item)))
                      }
                      variant="bordered"
                      className="flex-1"
                      classNames={{ inputWrapper: 'min-h-10' }}
                    />
                    <CtyButton
                      isIconOnly
                      size="sm"
                      variant="light"
                      color="danger"
                      aria-label={`Delete prompt ${index + 1}`}
                      onPress={() => {
                        promptKeys.current.splice(index, 1);
                        updatePrompts(prompts.filter((_, itemIndex) => itemIndex !== index));
                      }}
                    >
                      <Icon icon="solar:trash-bin-trash-linear" width={18} />
                    </CtyButton>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
